import { useEffect, useMemo, useState } from "react";
import { Download } from "lucide-react";
import { toast } from "sonner";
import { PageHead, Card } from "@/pages/app/AppShell";
import { api, fmtDate, errMsg, money } from "@/lib/api";

const AD_LABEL = { pending_review: "Waiting for review", live: "Live", ended: "Ended", rejected: "Rejected · refunded" };

function AdReview() {
  const [ads, setAds] = useState([]);
  const [busy, setBusy] = useState("");
  const load = () => api.get("/hq/ads").then((r) => setAds(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  useEffect(() => { load(); }, []);
  const act = async (a, kind) => {
    const reason = kind === "reject" ? window.prompt("Reason for rejecting (the company will see this):", "") : "";
    if (reason === null) return;
    setBusy(a.id + kind);
    try {
      await api.post(`/hq/ads/${a.id}/${kind}`, kind === "reject" ? { reason } : {});
      toast.success(kind === "approve" ? "Ad approved and scheduled" : "Ad rejected and refunded");
      load();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(""); }
  };
  const img = (u) => (u ? `${process.env.REACT_APP_BACKEND_URL}${u}` : "/img/summit.jpg");
  return (
    <Card className="p-6" data-testid="hq-ads">
      <h2 className="kicker">Featured ads</h2>
      {!ads.length && <p className="mt-3 text-sm text-slate-400" data-testid="hq-ads-empty">No ads booked yet.</p>}
      <div className="mt-3 divide-y divide-[#2A1240]/10">
        {ads.map((a) => (
          <div key={a.id} className="py-4 flex flex-col sm:flex-row gap-4" data-testid={`hq-ad-${a.id}`}>
            <img src={img(a.image_url)} alt="" className="h-20 w-32 rounded-lg object-cover" />
            <div className="flex-1 min-w-0">
              <p className="text-[#2A1240]">{a.title} <span className="text-xs text-slate-400">by {a.company_name}</span></p>
              <p className="text-sm text-slate-300">{a.text}</p>
              <p className="mt-1 text-xs text-slate-400">{a.cta_label} → {a.link} · {a.weeks} wk · {money(a.amount, 0)} · {AD_LABEL[a.status] || a.status}{a.starts_at ? ` · ${fmtDate(a.starts_at)} – ${fmtDate(a.ends_at)}` : ""}{a.stats ? ` · ${a.stats.views} views · ${a.stats.clicks} clicks (${a.stats.ctr}%)` : ""}</p>
            </div>
            {a.status === "pending_review" && (
              <div className="flex gap-2 items-start">
                <button className="btn-gold !py-2 !px-4 !text-xs" disabled={!!busy} onClick={() => act(a, "approve")} data-testid={`hq-ad-approve-${a.id}`}>Approve</button>
                <button className="btn-ghost !py-2 !px-4 !text-xs" disabled={!!busy} onClick={() => act(a, "reject")} data-testid={`hq-ad-reject-${a.id}`}>Reject & refund</button>
              </div>
            )}
          </div>
        ))}
      </div>
    </Card>
  );
}

const RANGES = [7, 30, 90];

const Stat = ({ label, value, testid }) => (
  <Card className="p-5">
    <p className="text-sm text-slate-400">{label}</p>
    <p className="mt-2 font-display text-3xl text-[#2A1240]" data-testid={testid}>{value}</p>
  </Card>
);

function Chart({ series }) {
  const max = Math.max(1, ...series.map((s) => s.views));
  return (
    <Card className="p-5" >
      <div className="flex items-center justify-between">
        <p className="text-[#2A1240]">Traffic per day</p>
        <p className="text-xs text-slate-400"><span className="inline-block h-2 w-2 rounded-sm bg-[#E0218A] mr-1" />Page views <span className="inline-block h-2 w-2 rounded-full bg-emerald-400 ml-3 mr-1" />Sign-ups</p>
      </div>
      <div className="mt-5 flex h-48 items-end gap-[3px]" data-testid="hq-chart">
        {series.map((s) => (
          <div key={s.day} className="group relative flex-1 h-full flex flex-col justify-end items-center">
            {s.signups > 0 && <span className="mb-1 h-2 w-2 rounded-full bg-emerald-400" />}
            <div className="w-full rounded-t bg-[#E0218A]/80 group-hover:bg-[#8E2BC4]" style={{ height: `${(s.views / max) * 100}%`, minHeight: s.views ? 2 : 0 }} />
            <div className="pointer-events-none absolute bottom-full mb-2 hidden group-hover:block whitespace-nowrap rounded-md bg-[#FBE9F3] border border-[#2A1240]/10 px-2 py-1 text-xs text-slate-200 z-10">
              {fmtDate(s.day)}: {s.views} views, {s.visitors} visitors, {s.signups} sign-ups
            </div>
          </div>
        ))}
      </div>
      <div className="mt-2 flex justify-between text-xs text-slate-400"><span>{fmtDate(series[0]?.day)}</span><span>{fmtDate(series[series.length - 1]?.day)}</span></div>
    </Card>
  );
}

const TopList = ({ title, rows, empty, testid }) => (
  <Card className="p-5" >
    <p className="text-[#2A1240]">{title}</p>
    <div className="mt-4 space-y-2" data-testid={testid}>
      {!rows.length && <p className="text-sm text-slate-400">{empty}</p>}
      {rows.map((r) => (
        <div key={r.key} className="flex items-center justify-between gap-3 text-sm">
          <span className="truncate text-slate-200">{r.key}</span>
          <span className="shrink-0 text-slate-400">{r.views} views · {r.visitors} people</span>
        </div>
      ))}
    </div>
  </Card>
);

function exportCsv(rows) {
  const head = ["Name", "Email", "Company", "Type", "Role", "Signed up"];
  const lines = rows.map((u) => [u.name, u.email, u.company, u.company_type, u.role, u.created_at].map((v) => `"${String(v || "").replace(/"/g, '""')}"`).join(","));
  const blob = new Blob([[head.join(","), ...lines].join("\n")], { type: "text/csv" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = "balqis-book-signups.csv";
  a.click();
  URL.revokeObjectURL(a.href);
}

function Signups({ rows }) {
  const [q, setQ] = useState("");
  const list = useMemo(() => rows.filter((u) => `${u.name} ${u.email} ${u.company}`.toLowerCase().includes(q.toLowerCase())), [rows, q]);
  return (
    <Card className="p-5">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <p className="text-[#2A1240]">Who signed up</p>
        <div className="flex gap-2">
          <input className="field !py-2 sm:w-64" placeholder="Search name, email or company" value={q} onChange={(e) => setQ(e.target.value)} data-testid="hq-signup-search" />
          <button className="btn-ghost !py-2 !px-3 !text-xs" onClick={() => exportCsv(list)} data-testid="hq-export-csv"><Download className="h-3.5 w-3.5" />CSV</button>
        </div>
      </div>
      <div className="mt-4 overflow-x-auto">
        <table className="w-full text-sm" data-testid="hq-signups-table">
          <thead><tr className="text-left text-slate-400 border-b border-[#2A1240]/10"><th className="py-2 pr-4 font-normal">Name</th><th className="py-2 pr-4 font-normal">Email</th><th className="py-2 pr-4 font-normal">Company</th><th className="py-2 font-normal">Signed up</th></tr></thead>
          <tbody>
            {list.map((u) => (
              <tr key={u.id} className="border-b border-[#2A1240]/5" data-testid={`hq-signup-${u.id}`}>
                <td className="py-2.5 pr-4 text-[#2A1240]">{u.name}{u.role === "admin" && <span className="ml-2 text-xs text-[#8E2BC4]">admin</span>}</td>
                <td className="py-2.5 pr-4 text-slate-300">{u.email}</td>
                <td className="py-2.5 pr-4 text-slate-300">{u.company}{u.company_type && <span className="text-slate-400"> · {u.company_type}</span>}</td>
                <td className="py-2.5 text-slate-400 whitespace-nowrap">{u.created_at ? fmtDate(u.created_at) : "-"}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {!list.length && <p className="py-6 text-center text-sm text-slate-400">No one matches that search.</p>}
      </div>
    </Card>
  );
}

export default function HQ() {
  const [days, setDays] = useState(30);
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => {
    api.get("/hq", { params: { days } }).then((r) => { setD(r.data); setErr(""); }).catch(() => setErr("Could not load HQ data."));
  }, [days]);
  const t = d?.totals || {};
  return (
    <div data-testid="hq-page">
      <PageHead kicker="Admin" title="HQ">
        <div className="flex rounded-lg border border-[#2A1240]/10 p-1">
          {RANGES.map((r) => (
            <button key={r} onClick={() => setDays(r)} className={`rounded-md px-3 py-1.5 text-sm ${days === r ? "bg-[#E0218A] text-white" : "text-slate-300 hover:text-[#2A1240]"}`} data-testid={`hq-range-${r}`}>{r} days</button>
          ))}
        </div>
      </PageHead>
      {err && <p className="text-sm text-red-300" data-testid="hq-error">{err}</p>}
      {d && (
        <div className="space-y-5">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <Stat label={`Visitors (${days} days)`} value={t.visitors ?? 0} testid="hq-visitors" />
            <Stat label={`Page views (${days} days)`} value={t.views ?? 0} testid="hq-views" />
            <Stat label={`Sign-ups (${days} days)`} value={t.signups ?? 0} testid="hq-signups-count" />
            <Stat label="Total members" value={`${t.users ?? 0} people · ${t.companies ?? 0} companies`} testid="hq-members" />
          </div>
          <p className="text-sm text-slate-400" data-testid="hq-today">Today: {t.today_visitors ?? 0} visitors and {t.today_views ?? 0} page views. {t.mobile_pct ?? 0}% of views in this period came from phones. Your own visits as admin aren't counted.</p>
          <Chart series={Array.isArray(d.series) ? d.series : []} />
          <div className="grid lg:grid-cols-2 gap-5">
            <TopList title="Most visited pages" rows={d.top_pages || []} empty="No visits yet." testid="hq-top-pages" />
            <TopList title="Where visitors came from" rows={d.top_referrers || []} empty="No outside referrers yet. Direct visits aren't listed." testid="hq-top-referrers" />
          </div>
          <AdReview />
          <Signups rows={Array.isArray(d.signups) ? d.signups : []} />
        </div>
      )}
    </div>
  );
}
